import apiClient from "../../client/apiClient"
import { appendQueryParams } from "../../utils"

export interface Driver {
  _id: string
  first_name: string
  last_name: string
  contact_number: string
  license_number: string
  address: string
  status: string
  driverID?: string
}

export interface Van {
  _id: string
  key: string
  id: string
  registration_number: string
  van_type?: string
  model: string
  capacity: number
  route_name: { name: string; _id?: string }[]
  driver_id?: string
  driver?: Driver
  status: "active" | "maintenance" | "inactive"
  vanId: string
  branch_id?: string
  registrationImage?: string
  contractorId?: {
    _id?: string
    name?: string
    contractorId?: string
    commissionPercentage?: number
    contractorType?: string
  }
  createdAt?: string
  updatedAt?: string
}

export type PaginationData = {
  currentPage: number
  hasNextPage: boolean
  hasPrevPage: boolean
  limit: number
  total: number
  totalPages: number
}

export type VansResponse = {
  data: Van[]
  pagination: PaginationData
}

const TRANSPORT_VEHICLE_ENDPOINT = {
  VANS: "/van",
}

const getAllVans = async ({
  page = 1,
  limit = 20,
  searchText,
  status,
  sortBy,
  sortOrder,
}: {
  page?: number
  limit?: number
  searchText?: string
  status?: string
  sortBy?: string
  sortOrder?: string
} = {}): Promise<VansResponse> => {
  const response = await apiClient({
    url: appendQueryParams(TRANSPORT_VEHICLE_ENDPOINT.VANS, {
      page,
      limit,
      searchText,
      status,
      sortBy,
      sortOrder,
    }),
    method: "GET",
  })
  return response.data
}

/** Backend expects shiftRoutes as { shiftId, route } and contractorId, vehicleNo, chassisNo, engineNo, maker */
const createVan = async (vanData: {
  registration_number?: string
  van_type?: string
  vanType?: string
  type?: string
  model?: string
  capacity: number
  route_name?: { name: string }[]
  driver?: string
  status?: string
  contractor?: string
  contractorId?: string
  chassis_number?: string
  chassisNo?: string
  engine_number?: string
  engineNo?: string
  shift_routes?: { shiftId: string; routeName?: string; route?: string }[]
  shiftRoutes?: { shiftId: string; route: string }[]
  vehicleNo?: string
  maker?: string
  vehicleType?: string
  [key: string]: unknown
}) => {
  const payload: Record<string, unknown> = { ...vanData }
  if (payload.contractor != null) {
    payload.contractorId = payload.contractor
    delete payload.contractor
  }
  if (payload.chassis_number != null) {
    payload.chassisNo = payload.chassis_number
    delete payload.chassis_number
  }
  if (payload.engine_number != null) {
    payload.engineNo = payload.engine_number
    delete payload.engine_number
  }
  if (Array.isArray(payload.shift_routes)) {
    payload.shiftRoutes = (
      payload.shift_routes as {
        shiftId: string
        routeName?: string
        route?: string
      }[]
    )
      .map(sr => ({
        shiftId: sr.shiftId,
        route: (sr.route ?? sr.routeName ?? "").toString().trim(),
      }))
      .filter(sr => sr.route)
    delete payload.shift_routes
  }
  if (payload.registration_number != null && payload.vehicleNo == null) {
    payload.vehicleNo = payload.registration_number
  }
  // Keep backend vehicleType in sync with selected van_type if provided
  if (payload.van_type != null && payload.vehicleType == null) {
    payload.vehicleType = payload.van_type
  }
  if (payload.model != null && payload.maker == null) {
    payload.maker = payload.model
  }
  const response = await apiClient({
    url: TRANSPORT_VEHICLE_ENDPOINT.VANS,
    method: "POST",
    data: payload,
  })
  return response.data
}

const updateVan = async (
  vanId: string,
  vanData: {
    registration_number?: string
    van_type?: string
    model?: string
    capacity?: number
    route_name?: { name: string }[]
    driver?: string
    status?: string
    contractor?: string
    contractorId?: string
    chassis_number?: string
    chassisNo?: string
    engine_number?: string
    engineNo?: string
    shift_routes?: { shiftId: string; routeName?: string; route?: string }[]
    shiftRoutes?: { shiftId: string; route: string }[]
    [key: string]: unknown
  },
) => {
  const payload: Record<string, unknown> = { ...vanData }
  if (payload.contractor != null) {
    payload.contractorId = payload.contractor
    delete payload.contractor
  }
  if (payload.chassis_number != null) {
    payload.chassisNo = payload.chassis_number
    delete payload.chassis_number
  }
  if (payload.engine_number != null) {
    payload.engineNo = payload.engine_number
    delete payload.engine_number
  }
  if (Array.isArray(payload.shift_routes)) {
    payload.shiftRoutes = (
      payload.shift_routes as {
        shiftId: string
        routeName?: string
        route?: string
      }[]
    )
      .map(sr => ({
        shiftId: sr.shiftId,
        route: (sr.route ?? sr.routeName ?? "").toString().trim(),
      }))
      .filter(sr => sr.route)
    delete payload.shift_routes
  }
  // Keep backend vehicleType in sync with selected van_type if provided
  if (payload.van_type != null && (payload as any).vehicleType == null) {
    ;(payload as any).vehicleType = payload.van_type
  }
  const response = await apiClient({
    url: `${TRANSPORT_VEHICLE_ENDPOINT.VANS}/${vanId}`,
    method: "PUT",
    data: payload,
  })
  return response.data
}

const deleteVan = async (vanId: string) => {
  const response = await apiClient({
    url: `${TRANSPORT_VEHICLE_ENDPOINT.VANS}/${vanId}`,
    method: "DELETE",
  })
  return response.data
}

/** Upload vehicle documents (car registration + govt approval images).
 * Backend: PATCH /van/:id/documents, multer fields: carRegistration, govtApproval */
const uploadVanDocuments = async (
  vanId: string,
  formData: FormData,
): Promise<{ registrationImage?: string; govtApprovalImage?: string }> => {
  const response = await apiClient({
    url: `${TRANSPORT_VEHICLE_ENDPOINT.VANS}/${vanId}/documents`,
    method: "PATCH",
    data: formData,
  })
  return response.data?.data ?? response.data ?? {}
}

export type VanTypesOption = { value: string; label: string }

// NOTE: Backend returns `vanTypes` inside `/van` response (getVans),
// so there is no separate `GET /van/types` call needed from frontend.
export type VansResponseWithTypes = VansResponse & {
  vanTypes?: VanTypesOption[]
}

const deleteVanDocument = async (vanId: string): Promise<void> => {
  await apiClient({
    url: `${TRANSPORT_VEHICLE_ENDPOINT.VANS}/${vanId}/documents`,
    method: "DELETE",
  })
}

export { getAllVans, createVan, updateVan, deleteVan, uploadVanDocuments, deleteVanDocument }
